import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useEpaFlag } from 'utils/featureFlags';
import {
  EPA_GULF_LINKED_EVENT_NAME,
  EPA_GULF_ORG_EVENT_DEMO_KEY,
  EPA_GULF_RESOURCES_COMMITTED,
  EPA_GULF_THRESHOLD_DESCRIPTION,
  type EpaGulfFacility,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/epaGulfThreatNotification';
import { orgEventKeys, type OrgEvent } from 'hooks/orgEvents/orgEventHooks';
import { queryNplSitesInHeleneCone } from './queryNplSitesInHeleneCone';

const EPA_GULF_EVENT_REGION = 'FEMA Region IV';

const ensureLocks = new Map<string, Promise<unknown>>();

function epaGulfCentroid(sites: EpaGulfFacility[]): {
  lon: number;
  lat: number;
} {
  if (sites.length === 0) {
    return { lon: -90.0, lat: 29.8 };
  }
  const lon = sites.reduce((sum, f) => sum + f.lon, 0) / sites.length;
  const lat = sites.reduce((sum, f) => sum + f.lat, 0) / sites.length;
  return { lon, lat };
}

async function patchEpaGulfOrgEventIfStale(
  existing: OrgEvent,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrgEvent> {
  const needsPatch =
    existing.name !== EPA_GULF_LINKED_EVENT_NAME ||
    existing.resources_committed !== EPA_GULF_RESOURCES_COMMITTED ||
    existing.region !== EPA_GULF_EVENT_REGION;
  if (!needsPatch) {
    return existing;
  }

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: EPA_GULF_LINKED_EVENT_NAME,
      resources_committed: EPA_GULF_RESOURCES_COMMITTED,
      region: EPA_GULF_EVENT_REGION,
    }
  );
  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(existing.group_guid),
  });
  return patched.data;
}

async function ensureEpaGulfOrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === EPA_GULF_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return patchEpaGulfOrgEventIfStale(existing, queryClient);
  }

  const sites = await queryNplSitesInHeleneCone();
  const { lon, lat } = epaGulfCentroid(sites);
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: EPA_GULF_LINKED_EVENT_NAME,
    type: 'Tropical Storm',
    status: 'Active',
    severity: 'High',
    region: EPA_GULF_EVENT_REGION,
    business_unit: 'EPA',
    summary:
      'Threshold-triggered tropical storm event for NPL Superfund sites inside the Helene wind swath.',
    resources_committed: EPA_GULF_RESOURCES_COMMITTED,
    lon,
    lat,
    started_at: '2024-09-24T12:00:00.000Z',
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: EPA_GULF_THRESHOLD_DESCRIPTION,
    demo_key: EPA_GULF_ORG_EVENT_DEMO_KEY,
  });

  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });

  return created.data;
}

export function useEnsureEpaGulfOrgEvent() {
  const epaEnabled = useEpaFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!epaEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `epa-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureEpaGulfOrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure EPA Gulf org event', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [epaEnabled, groupGuid, userGuid, queryClient]);
}
